import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, FileTransfersService, IOcbFileTransfer, IOcbUser } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-file-transfers-archived-files-accesses',
    templateUrl: './file-transfers-archived-files-accesses.component.html',
    styleUrls: ['./file-transfers-archived-files-accesses.component.scss'],
})
export class FileTransfersArchivedFilesAccessesComponent implements AfterViewInit {

    @ViewChild('accessesGrid')
    public accessesGrid: DxDataGridComponent;

    @ViewChild('accessValidation')
    public accessValidationGroup: DxValidationGroupComponent;

    // Main Data-Grid
    public dataSource: IOcbUser[] = [];

    // List of file transfers
    public fileTransfers: IOcbFileTransfer[] = null;
    public selectedFileTransfer: IOcbFileTransfer = null;

    // Popup create
    public newEmployeeAccess: IOcbUser = null;

    /**
     * Boolean to open or close the types dropdown
     */
    public isDropDownBoxOpened: boolean = false;

    /**
     * Boolean to show or hide the create popup
     */
    public isPopupVisible: boolean = false;

    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = true;

    /**
     * Name of the current environment
     */
    public currentEnvironmentName: string = '';

    /**
     * Internal identifier of the current file transfer
     */
    private currentEnvironmentId: string = '';

    constructor(
        private route: ActivatedRoute,
        private environmentsService: EnvironmentsService,
        private fileTransfersService: FileTransfersService,
        private translateService: TranslateService) {
    }

    public ngAfterViewInit(): void {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => {
                this.currentEnvironmentName = env.name;
            });
            this.loadFileTransfers();
        });
    }

    /**
     * Configure toolbar items
     */
    public onToolbarPreparing(e): void {
        // Create a custom Add & Refresh buttons in the toolbar
        forkJoin(
            // Get translation messages
            this.translateService.get('_commons.add'),
            this.translateService.get('_commons.refresh'),
        ).subscribe(
            messages => {
                e.toolbarOptions.items.unshift(
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            icon: 'add',
                            text: messages[0],
                            onClick: () => this.displayNewPopup(),
                        },
                    }, {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            icon: 'refresh',
                            text: messages[1],
                            onClick: () => this.loadDataSource(),
                        },
                    });
            },
            err => this.handleError(err),
        );
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'trash', onItemClick: (ev) => this.deleteAccess(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Dipslay new popup
     */
    public displayNewPopup(): void {
        if (this.selectedFileTransfer) {
            this.newEmployeeAccess = null;
            this.isPopupVisible = true;
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired')
                .subscribe(message => {
                    notify(message, 'error', 3000);
                });
        }
    }

    /**
     * Add new employee access
     */
    public createAccess(): void {
        if (this.accessValidationGroup.instance.validate().isValid) {
            this.showLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => this.fileTransfersService.addArchivedFilesAccess(this.selectedFileTransfer.id, this.newEmployeeAccess.id)),
                    mergeMap(_ => this.translateService.get('fileTransfersArchivedFilesAccesses.msgAddComplete')),
                    finalize(() => {
                        this.accessesGrid.instance.endCustomLoading();
                        this.isPopupVisible = false;
                    }))
                .subscribe(
                    message => {
                        this.loadDataSource();
                        // Notify
                        notify(message, 'success', 3000);
                    },
                    err => this.handleError(err));
        }
    }

    /**
     * Delete an employee access
     * @param e Handle event
     * @param employee Employee to remove from accesses list
     */
    public deleteAccess(e, employee: IOcbUser): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.showLoading$('_commons.loading'),
            this.translateService.get('fileTransfersArchivedFilesAccesses.msgDeleteConfirm', { name: employee.name }),
            this.translateService.get('fileTransfersArchivedFilesAccesses.msgDeleteConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
                filter(accepted => accepted),
                // Send the DELETE request
                mergeMap(_ => this.fileTransfersService.deleteArchivedFilesAccess(this.selectedFileTransfer.id, employee.id)),
                // Get the confirmation message
                mergeMap(_ => this.translateService.get('fileTransfersArchivedFilesAccesses.msgDeleteComplete')),
                finalize(() => this.accessesGrid.instance.endCustomLoading()))
            .subscribe(
                message => {
                    this.loadDataSource();
                    // Notify the success
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
    }

    /**
     * Change file transfer filter
     * @param e Handle event
     */
    public filterChanged(e): void {
        if (e) {
            this.selectedFileTransfer = e;
            this.isDropDownBoxOpened = false;
            this.loadDataSource();
        } else {
            this.dataSource = [];
        }
    }

    /**
     * Load current environment file transfers
     */
    private loadFileTransfers(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, true)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                res => {
                    this.fileTransfers = res;
                },
                err => this.handleError(err));
    }

    /**
     * Load data source with the archived files accesses for current file transfer
     */
    private loadDataSource(): void {
        if (this.selectedFileTransfer) {
            this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.fileTransfersService.getArchivedFilesAccess(this.selectedFileTransfer.id)),
                finalize(() => this.accessesGrid.instance.endCustomLoading()))
            .subscribe(
                res => this.dataSource = res,
                err => this.handleError(err));
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired')
                .subscribe(message => {
                    notify(message, 'error', 3000);
                });
        }
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.accessesGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            }
            else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            }
        }
        else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
